import type { LocalizationSettings } from "./localization"

export interface LocalizationFormatters {
  number(value: number, options?: Intl.NumberFormatOptions): string
  currency(value: number, currency: string, options?: Intl.NumberFormatOptions): string
  /** A date without a time zone; Date arguments contribute their local calendar fields. */
  dateOnly(value: Date | string, options?: Intl.DateTimeFormatOptions): string
  /** An instant displayed in the selected time zone. */
  dateTime(value: Date | string | number, options?: Intl.DateTimeFormatOptions): string
  list(values: Iterable<string>, options?: Intl.ListFormatOptions): string
  relativeTime(value: number, unit: Intl.RelativeTimeFormatUnit, options?: Intl.RelativeTimeFormatOptions): string
  compare(first: string, second: string, options?: Intl.CollatorOptions): number
}

const numberFormatOptionNames = [
  "localeMatcher",
  "numberingSystem",
  "style",
  "currency",
  "currencyDisplay",
  "currencySign",
  "unit",
  "unitDisplay",
  "notation",
  "compactDisplay",
  "useGrouping",
  "signDisplay",
  "minimumIntegerDigits",
  "minimumFractionDigits",
  "maximumFractionDigits",
  "minimumSignificantDigits",
  "maximumSignificantDigits",
  "roundingPriority",
  "roundingIncrement",
  "roundingMode",
  "trailingZeroDisplay",
] as const

function numberFormatOptions(options: Intl.NumberFormatOptions): Intl.NumberFormatOptions {
  // i18next also supplies interpolation values and message metadata. They do not
  // affect Intl output and must not create a new cache entry for every grid row.
  // Include modern Intl options even when the project's TypeScript lib predates them.
  const values = options as Record<string, unknown>
  return Object.fromEntries(
    numberFormatOptionNames.filter((key) => values[key] !== undefined).map((key) => [key, values[key]])
  )
}

/** Caches are bounded and belong to a culture snapshot, never to column definitions. */
function formatterCache<TOptions extends object, TFormatter>(create: (options: TOptions) => TFormatter) {
  const cache = new Map<string, TFormatter>()
  return (options: TOptions) => {
    const key = JSON.stringify(Object.entries(options).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
    let formatter = cache.get(key)
    if (!formatter) {
      formatter = create(options)
      if (cache.size >= 100) cache.delete(cache.keys().next().value!)
      cache.set(key, formatter)
    }
    return formatter
  }
}

export function createFormatting(settings: LocalizationSettings): LocalizationFormatters {
  const cachedNumber = formatterCache(
    (options: Intl.NumberFormatOptions) => new Intl.NumberFormat(settings.culture, options)
  )
  const number = (options: Intl.NumberFormatOptions) => cachedNumber(numberFormatOptions(options))
  const date = formatterCache(
    (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(settings.culture, options)
  )
  const list = formatterCache((options: Intl.ListFormatOptions) => new Intl.ListFormat(settings.culture, options))
  const relative = formatterCache(
    (options: Intl.RelativeTimeFormatOptions) => new Intl.RelativeTimeFormat(settings.culture, options)
  )
  const collator = formatterCache((options: Intl.CollatorOptions) => new Intl.Collator(settings.culture, options))

  return Object.freeze({
    number: (value: number, options: Intl.NumberFormatOptions = {}) => number(options).format(value),
    currency: (value: number, currency: string, options: Intl.NumberFormatOptions = {}) =>
      number({ ...options, style: "currency", currency }).format(value),
    dateOnly: (value: Date | string, options: Intl.DateTimeFormatOptions = {}) =>
      date({ ...options, calendar: "gregory", timeZone: "UTC" }).format(toCalendarDate(value)),
    dateTime: (value: Date | string | number, options: Intl.DateTimeFormatOptions = {}) => {
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        throw new RangeError("Use dateOnly to format a date without a time zone.")
      }
      if (typeof value === "string" && !/^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) {
        throw new RangeError("Timestamp strings must be ISO 8601 values with Z or an explicit offset.")
      }
      const instant = value instanceof Date ? value : new Date(value)
      if (!Number.isFinite(instant.getTime())) throw new RangeError("Invalid timestamp.")
      const fields = [
        "dateStyle",
        "timeStyle",
        "weekday",
        "era",
        "year",
        "month",
        "day",
        "dayPeriod",
        "hour",
        "minute",
        "second",
        "fractionalSecondDigits",
        "timeZoneName",
      ]
      const hasExplicitFields = Object.entries(options).some(
        ([key, value]) => fields.includes(key) && value !== undefined
      )
      const display = hasExplicitFields
        ? options
        : { dateStyle: "short" as const, timeStyle: "short" as const, ...options }
      return date({ timeZone: settings.timeZone, ...display }).format(instant)
    },
    list: (values: Iterable<string>, options: Intl.ListFormatOptions = {}) => list(options).format(values),
    relativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit, options: Intl.RelativeTimeFormatOptions = {}) =>
      relative(options).format(value, unit),
    compare: (first: string, second: string, options: Intl.CollatorOptions = {}) =>
      collator(options).compare(first, second),
  })
}

function toCalendarDate(value: Date | string): Date {
  let year: number
  let month: number
  let day: number
  if (value instanceof Date) {
    year = value.getFullYear()
    month = value.getMonth() + 1
    day = value.getDate()
  } else {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) throw new RangeError("A date-only string must have the form yyyy-MM-dd.")
    year = Number(match[1])
    month = Number(match[2])
    day = Number(match[3])
  }
  const date = new Date(0)
  date.setUTCHours(0, 0, 0, 0)
  date.setUTCFullYear(year, month - 1, day)
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new RangeError("Invalid calendar date.")
  }
  return date
}
